import { capitalize, isNil } from "lodash";
import moment from "moment";
import { DateRange } from "moment-range";
import type { TFunction } from "src/design-system/core/TranslationContext";
import { COMMON_TRANSLATION_NAMESPACE } from "src/i18n/config";
import { type Offer, type OfferDiscount, OfferTypeEnum } from "src/types/offer";
import type { ItemNameCategoryDiscount } from "src/types/offer/ItemNameCategoryDiscount";
import type { OfferScenario } from "src/types/offer/OfferScenario";
import type { SponsoredAdScenario } from "src/types/sponsored-ads/SponsoredAdScenario";
import { MarketingActionActivationTypeEnum } from "src/utils/activationTypes";
import {
    formatBid,
    formatPrice,
    formatSlotStringWithTimeZoneWithWordLink,
    type FormatterOptions,
    PERCENT_FACTOR,
} from "src/utils/formatters";
import { UNKNOWN_CATEGORY_NAME } from "src/views/marketing-actions/components/form/OfferCharacteristicCard/OfferCharacteristicSharedInput";
import {
    MARKETING_ACTIONS_CREATE_VIEW_TK,
    MARKETING_ACTIONS_TRANSLATION_NAMESPACE,
} from "src/views/marketing-actions/config";
import { SPONSORED_AD_TRANSLATION_NAMESPACE } from "src/views/sponsored-ads/config";
import { SPONSORED_AD_COMMON_TAB_TK } from "src/views/sponsored-ads/sponsored-ads-tables-view/config";

function generateStringOfDiscountItem(
    itemDiscounts: {
        name: string;
        discount?: Pick<OfferDiscount, "percentDiscount">;
    }[]
): string {
    return (itemDiscounts || [])
        .map((itemDiscount) => {
            if (itemDiscount?.discount?.percentDiscount) {
                return `${itemDiscount.name} (${itemDiscount.discount?.percentDiscount / PERCENT_FACTOR}%)`;
            }
            return `${itemDiscount?.name}`;
        })
        .join(", ");
}

const groupedItemsByCategories = (items, t) => {
    return (
        items?.reduce(
            (acc, itemDiscount) => {
                const generalCategory = t(
                    `${MARKETING_ACTIONS_TRANSLATION_NAMESPACE}:${MARKETING_ACTIONS_CREATE_VIEW_TK}.category.general`
                );
                let category =
                    (itemDiscount?.categoryName === undefined &&
                        itemDiscount?.remoteCategory === undefined) ||
                    itemDiscount?.categoryName === UNKNOWN_CATEGORY_NAME
                        ? generalCategory
                        : itemDiscount?.categoryName;

                if (isNil(category)) {
                    category = itemDiscount?.remoteCategory?.name ?? generalCategory;
                }

                if (!acc[category]) {
                    acc[category] = [];
                }
                acc[category].push(itemDiscount);
                return acc;
            },
            {} as Record<string, typeof items>
        ) ?? {}
    );
};

// Cette fonction est utilisé pour creer une string qui affiche les items avec les categories.
// C'est une string d'une seul ligne qui est retourné c'est utile dans certain cas
// notamment dans la page performance des offres pour afficher les produits sans tooltip et couper la ligne avec des "..." dans le cas ou c'est trop long
// C'est aussi utilisé dans la visualisation des SS car c'est encore les anciennes tooltips (ca va changer apres)
//
// Il faut essayer de ne plus trop utiliser cette fonction mais formatScenarioItemsToItemListGroupedByCategory a la place.
//
export function formatOfferDiscountItems(
    items: (Pick<ItemNameCategoryDiscount, "name" | "categoryName"> & {
        discount?: Pick<OfferDiscount, "percentDiscount">;
        remoteCategory?: {
            name: string;
        };
    })[],
    t: TFunction,
    { joinString = "\n\n" }: { joinString?: string } = {}
): string {
    const itemDiscountGroupedByCategory = groupedItemsByCategories(items, t);

    return Object.keys(itemDiscountGroupedByCategory)
        .map((category) => {
            const itemDiscounts = itemDiscountGroupedByCategory[category];
            return `${category ?? UNKNOWN_CATEGORY_NAME} : ${generateStringOfDiscountItem(itemDiscounts)}`;
        })
        .join(joinString);
}

export function formatOfferItemsByOfferType(
    offerType: OfferTypeEnum,
    items: (Pick<ItemNameCategoryDiscount, "name" | "categoryName"> & {
        discount?: Pick<OfferDiscount, "percentDiscount">;
        remoteCategory?: {
            name: string;
        };
    })[],
    t: TFunction,
    { joinString = "\n" }: { joinString?: string } = {}
): string {
    const groupedItemList = formatScenarioItemsToItemListGroupedByCategory(items, t);

    return groupedItemList
        .map((groupItems) => {
            const { categoryName, items } = groupItems;
            return `${categoryName ?? UNKNOWN_CATEGORY_NAME} : ${
                offerType === OfferTypeEnum.DiscountByItem
                    ? generateStringOfDiscountItem(items)
                    : items.map((i) => i.name).join(", ")
            }`;
        })
        .join(joinString);
}

// fonction qui permet de faire une liste de string des category names utilisé dans le display des produits dans la page performance
// préférer ne pas utiliser cette fonction pour display les tooltip car il existe ItemCategoryListTooltipContent pour ca
// c'est une utilisation pour un cas spécifique comme pour la fonction formatOfferDiscountItems
export function formatOfferDiscountCategoryItemList(itemCategoryList, t: TFunction) {
    const nameList = itemCategoryList.map((e) => {
        return e.name === UNKNOWN_CATEGORY_NAME
            ? t(`${COMMON_TRANSLATION_NAMESPACE}:category.general`)
            : e.name;
    });
    return nameList.join(", ");
}

// c'est une fonction qui permet de faire a peut pret la meme chose que formatOfferDiscountItems mais en retournant
// un objet a la place. Ca permet de pouvoir faire des variations de style entre les categories et les items
// pour l'affichages des tooltip
//
// il faut privilégier l'utilisation de formatScenarioItemsToItemListGroupedByCategory le plus possible
//
export function formatScenarioItemsToItemListGroupedByCategory(
    items: (Pick<ItemNameCategoryDiscount, "name" | "categoryName"> & {
        discount?: Pick<OfferDiscount, "percentDiscount">;
        remoteCategory?: {
            name: string;
        };
    })[],
    t: TFunction
) {
    const itemDiscountGroupedByCategory = groupedItemsByCategories(items, t);

    return Object.keys(itemDiscountGroupedByCategory).map((category) => ({
        categoryName: category,
        items: itemDiscountGroupedByCategory[category],
    }));
}

export function formatScenarioType(scenario: Pick<OfferScenario, "type">, t: TFunction): string {
    return t(`${COMMON_TRANSLATION_NAMESPACE}:enums.offer-type.${scenario?.type}`);
}

export function formatScenarioTypeWithItems(
    scenario: Pick<OfferScenario, "type" | "items">,
    t: TFunction
): string {
    let offerType = formatScenarioType(scenario, t);

    if (scenario?.items.length > 0) {
        offerType += ` - ${formatOfferDiscountItems(scenario.items, t)}`;
    }
    return offerType;
}

export function formatOfferType(offer: Offer, t: TFunction): string {
    return offer.activationTypes.includes(MarketingActionActivationTypeEnum.SmartScheduler)
        ? t(`${COMMON_TRANSLATION_NAMESPACE}:enums.offer-type.auto`)
        : formatScenarioType(offer.mainScenario, t);
}

export function formatOfferTypeWithItems(
    offer: Offer,
    scenario: OfferScenario | undefined,
    t: TFunction
): string {
    let offerType = formatOfferType(offer, t);

    if (scenario?.items.length > 0) {
        offerType += ` - ${formatOfferDiscountItems(scenario.items, t)}`;
    }
    return offerType;
}

// TODO: remove it is only for excel so go to the api
export function formatDuoOffer(
    scenario: Pick<SponsoredAdScenario, "bid" | "slotBudget">,
    formatOptions: FormatterOptions
): string {
    const { translateRaw } = formatOptions;
    return `${capitalize(translateRaw(`${COMMON_TRANSLATION_NAMESPACE}:concepts.duo`))} - ${translateRaw(
        `${SPONSORED_AD_TRANSLATION_NAMESPACE}:${SPONSORED_AD_COMMON_TAB_TK}.headers.columns.bid`
    )} : ${formatBid(scenario.bid, formatOptions)} / ${translateRaw(
        `${SPONSORED_AD_TRANSLATION_NAMESPACE}:${SPONSORED_AD_COMMON_TAB_TK}.headers.columns.slot-budget`
    )} : ${formatPrice(scenario.slotBudget, formatOptions)}`;
}

export const computePreorderTooltipStringWithTimezone = (
    startDateUTC: Date,
    endDateUTC: Date,
    scheduledStartDateUTC: Date,
    scheduledEndDateUTC: Date,
    timezone: string,
    translateCommon: TFunction,
    translateRaw: TFunction
): string => {
    const publicationPeriod = new DateRange(
        moment(scheduledStartDateUTC),
        moment(scheduledEndDateUTC)
    );
    const offerPeriod = new DateRange(moment(startDateUTC), moment(endDateUTC));
    const preorderPeriodList = publicationPeriod.subtract(offerPeriod);
    if (publicationPeriod.start < offerPeriod.start && preorderPeriodList.length > 0) {
        const preorderPeriod = preorderPeriodList[0];
        return formatSlotStringWithTimeZoneWithWordLink(
            preorderPeriod.start.utc().format(),
            preorderPeriod.end.utc().format(),
            undefined,
            timezone,
            { translateCommon, translateRaw, hasRoundDate: false }
        );
    }
};
